import React, { useCallback, useMemo } from 'react';

import type { PlaybookEditOp, PlaybookEditOpType } from '@aline/common';
import { PlaybookEditSchema } from '@aline/common';
import { link, routes } from '@aline/routes';
import { Typography } from '@mui/material';
import { Sparkles } from 'lucide-react';
import { Link, useMatch } from 'react-router-dom';
import { buildSuggestionModel } from 'src/components/admin/playbooks/detail/suggested-edits/suggestion-model';
import { PlaybookChip } from 'src/editor/toolbars/right-panel/ai/playbooks';
import { Apollo } from 'src/gql/generated';
import { useDispatchInfoSnack } from 'src/helpers/snackbars';
import { makeStyles } from 'tss-react/mui';
import { z } from 'zod';

import { AlineMarkdown } from '../../AlineMarkdown';
import type { MarkdownComponentProps } from '../../ChatPanelMarkdown';
import { getCodeBlockInfoFromNode } from '../../remark-plugins/remarkCodeBlock';

const useStyles = makeStyles()(theme => ({
  // The whole card is the link, so it keeps the card's text colors.
  clickable: {
    '&:hover, &:focus-visible': {
      borderColor: theme.palette.primary.main,
    },
    color: 'inherit',
    cursor: 'pointer',
    textDecoration: 'none',
    transition: theme.transitions.create('border-color'),
  },
  counts: {
    color: theme.palette.text.secondary,
    fontSize: 12,
  },
  link: {
    color: theme.palette.primary.main,
    fontSize: 12,
    fontWeight: 500,
    textDecoration: 'underline',
  },
  root: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.75),
    margin: theme.spacing(1, 0),
    padding: theme.spacing(1.5),
  },
  // The status chip, pushed to the right end of the title row.
  status: {
    display: 'inline-flex',
    marginLeft: 'auto',
  },
  titleRow: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1),
  },
}));

/** What the chat card counts: each op kind folded into one of these buckets. */
type CountKey =
  | 'categoriesAdded'
  | 'categoriesEdited'
  | 'categoriesRemoved'
  | 'positionsAdded'
  | 'positionsEdited'
  | 'positionsRemoved'
  | 'playbookEdited'
  | 'rulesAdded'
  | 'rulesEdited'
  | 'rulesRemoved';

const COUNT_KEY_BY_OP_TYPE: Record<Exclude<PlaybookEditOpType, 'note'>, CountKey> = {
  'create-category': 'categoriesAdded',
  'create-position': 'positionsAdded',
  'create-rule': 'rulesAdded',
  'delete-category': 'categoriesRemoved',
  'delete-position': 'positionsRemoved',
  'delete-rule': 'rulesRemoved',
  'edit-category': 'categoriesEdited',
  'edit-playbook': 'playbookEdited',
  'edit-position': 'positionsEdited',
  'edit-rule': 'rulesEdited',
  'move-category': 'categoriesEdited',
  'move-position': 'positionsEdited',
  'move-rule': 'rulesEdited',
};

// Phrased as what is proposed, not done: every op is a suggestion until it is accepted.
const COUNT_LABELS: Record<CountKey, [singular: string, plural: string]> = {
  categoriesAdded: ['category to add', 'categories to add'],
  categoriesEdited: ['category to edit', 'categories to edit'],
  categoriesRemoved: ['category to remove', 'categories to remove'],
  playbookEdited: ['playbook detail to edit', 'playbook details to edit'],
  positionsAdded: ['position to add', 'positions to add'],
  positionsEdited: ['position to edit', 'positions to edit'],
  positionsRemoved: ['position to remove', 'positions to remove'],
  rulesAdded: ['rule to add', 'rules to add'],
  rulesEdited: ['rule to edit', 'rules to edit'],
  rulesRemoved: ['rule to remove', 'rules to remove'],
};

/** The order the summary reads in: rules first, since they are what a playbook is. */
const COUNT_ORDER: CountKey[] = [
  'rulesEdited',
  'rulesAdded',
  'rulesRemoved',
  'positionsEdited',
  'positionsAdded',
  'positionsRemoved',
  'categoriesEdited',
  'categoriesAdded',
  'categoriesRemoved',
  'playbookEdited',
];

interface PlaybookEditSummary {
  counts: Partial<Record<CountKey, number>>;
  notes: string[];
  playbookId: string | null;
}

/** One JSONL line per op; a half-streamed last line simply does not count yet. */
export function parsePlaybookEditLines(rawJsonl: string): PlaybookEditOp[] {
  return rawJsonl.split('\n').flatMap(line => {
    if (!line.trim()) return [];
    try {
      const result = PlaybookEditSchema.safeParse(JSON.parse(line));
      return result.success ? [result.data.suggest] : [];
    } catch {
      return [];
    }
  });
}

export function summarizePlaybookEdits(ops: readonly PlaybookEditOp[]): PlaybookEditSummary {
  const counts: Partial<Record<CountKey, number>> = {};
  const notes: string[] = [];
  let playbookId: string | null = null;
  for (const op of ops) {
    playbookId ??= op.playbookId;
    if (op._type === 'note') notes.push(op.text);
    else {
      const key = COUNT_KEY_BY_OP_TYPE[op._type];
      counts[key] = (counts[key] ?? 0) + 1;
    }
  }
  return { counts, notes, playbookId };
}

export function formatPlaybookEditCounts(counts: Partial<Record<CountKey, number>>): string {
  return COUNT_ORDER.flatMap(key => {
    const count = counts[key];
    if (!count) return [];
    const [singular, plural] = COUNT_LABELS[key];
    return [`${count} ${count === 1 ? singular : plural}`];
  }).join(' • ');
}

/**
 * Where "Review the suggestions" goes. The playbook URL with no rule clears
 * the open rule and blanks the details panel, so the link keeps a rule when
 * it can: the one existing rule the suggestions name, or the rule already open.
 */
export function getReviewSuggestionsPath(options: {
  openRuleId: string | null;
  ops: readonly PlaybookEditOp[];
  playbookId: string;
}): string {
  const ruleId = getOnlyRuleId(options.ops) ?? options.openRuleId;
  return ruleId
    ? link.PlaybooksDetailRule({ playbookId: options.playbookId, ruleId })
    : link.PlaybooksDetail({ playbookId: options.playbookId });
}

export const NO_EDITS_LEFT_MESSAGE = 'No more suggested edits left to review';

/**
 * The card frame. The whole card links to the review, for a larger click
 * target; until an op has named the playbook there is nothing to open, and it
 * is a plain box.
 *
 * Once every suggestion has been decided the editor shows no suggestions bar,
 * so a click says so. The check reads from the network: the cache only
 * follows decisions while the playbook editor is open.
 */
const PlaybookEditCard = React.memo(function PlaybookEditCard({
  children,
  ops,
  playbookId,
}: {
  children: React.ReactNode;
  ops: readonly PlaybookEditOp[];
  playbookId: string | null;
}): JSX.Element {
  const { classes, cx } = useStyles();
  const ruleMatch = useMatch(routes.PlaybooksDetailRule);
  const [fetchPlaybook] = Apollo.useGetPlaybookDetailLazyQuery({ fetchPolicy: 'network-only' });
  const dispatchNoEditsLeftSnack = useDispatchInfoSnack({ message: NO_EDITS_LEFT_MESSAGE });
  const handleClick = useCallback(() => {
    if (!playbookId) return;
    void fetchPlaybook({ variables: { id: playbookId } }).then(({ data }) => {
      if (data?.playbook && buildSuggestionModel(data.playbook).stops.length === 0)
        dispatchNoEditsLeftSnack();
    });
  }, [dispatchNoEditsLeftSnack, fetchPlaybook, playbookId]);
  if (!playbookId) return <div className={classes.root}>{children}</div>;

  const matchedRuleId = ruleMatch?.params.ruleId;
  const openRuleId =
    ruleMatch?.params.playbookId === playbookId && matchedRuleId ? matchedRuleId : null;

  return (
    <Link
      className={cx(classes.root, classes.clickable)}
      to={getReviewSuggestionsPath({ openRuleId, ops, playbookId })}
      onClick={handleClick}
    >
      {children}
      <span className={classes.link}>Review the suggestions in the playbook editor</span>
    </Link>
  );
});

/**
 * The same chip the playbook generation card shows, in its running and
 * finished tones. "Suggesting" and "Suggested" rather than "Editing" and
 * "Edited": nothing in the playbook has changed until somebody accepts the
 * edits.
 */
const PlaybookEditStatusChip = React.memo(function PlaybookEditStatusChip({
  isSuggesting,
}: {
  isSuggesting: boolean;
}): JSX.Element {
  if (isSuggesting) return <PlaybookChip label="Suggesting" tone="running" />;

  return <PlaybookChip label="Suggested" tone="passed" />;
});

/**
 * The chat receipt for a playbook editor run. Ops that landed show as a
 * "Suggested playbook edits" card with counts derived from them; review
 * happens in the playbook editor, so the card links there rather than
 * listing individual suggestions. Notes are the agent's prose (an answer to
 * a question, or why nothing changed) and read as ordinary assistant
 * markdown under the card, or on their own when there were no edits, so a
 * plain answer never arrives inside a card saying nothing changed. While
 * nothing has landed yet there is nothing to show.
 *
 * The agent writes one line into the fence per edit and closes the fence when
 * it is done, so an open fence means more edits may still land. Until then
 * the card carries the same running chip as a playbook being generated, while
 * its counts climb, and the finished chip after.
 */
export const CodePlaybookEditElement = React.memo(function CodePlaybookEditElement({
  node,
}: MarkdownComponentProps): JSX.Element | null {
  const { classes } = useStyles();
  const codeBlockInfo = getCodeBlockInfoFromNode(node);
  const code = codeBlockInfo?.code ?? '';
  const ops = useMemo(() => parsePlaybookEditLines(code), [code]);
  const summary = useMemo(() => summarizePlaybookEdits(ops), [ops]);
  if (!codeBlockInfo) return null;

  const countsText = formatPlaybookEditCounts(summary.counts);
  const hasEdits = countsText.length > 0;
  if (!hasEdits && summary.notes.length === 0) return null;

  return (
    <>
      {hasEdits && (
        <PlaybookEditCard ops={ops} playbookId={summary.playbookId}>
          <div className={classes.titleRow}>
            <Sparkles size={16} />
            <Typography sx={{ fontWeight: 600 }}>Suggested playbook edits</Typography>
            <span className={classes.status}>
              <PlaybookEditStatusChip isSuggesting={codeBlockInfo.isNodeStreaming} />
            </span>
          </div>
          <Typography className={classes.counts}>{countsText}</Typography>
        </PlaybookEditCard>
      )}
      {summary.notes.map((note, index) => (
        <AlineMarkdown key={index} markdownString={note} type="message" />
      ))}
    </>
  );
});

const uuidSchema = z.uuid();

/** The one existing rule the ops name, or null when they name none or several. */
function getOnlyRuleId(ops: readonly PlaybookEditOp[]): string | null {
  let only: string | null = null;
  for (const op of ops) {
    const ruleId = getExistingRuleId(op);
    if (ruleId) {
      if (only && only !== ruleId) return null;
      only = ruleId;
    }
  }
  return only;
}

/**
 * The rule an op edits, when that id belongs to a saved rule. A new rule is
 * only a ref like "new-rule-1" until it is accepted, and the editor cannot
 * open a ref.
 */
function getExistingRuleId(op: PlaybookEditOp): string | null {
  switch (op._type) {
    case 'create-position':
    case 'delete-position':
    case 'delete-rule':
    case 'edit-position':
    case 'edit-rule':
    case 'move-position':
    case 'move-rule':
      return uuidSchema.safeParse(op.ruleId).success ? op.ruleId : null;
    case 'create-category':
    case 'create-rule':
    case 'delete-category':
    case 'edit-category':
    case 'edit-playbook':
    case 'move-category':
    case 'note':
      return null;
  }
}
